/**
 * Chat-turn performance tracer.
 *
 * Captures per-stage latency for a single chat turn (embedding, AI search,
 * LLM call, etc.) and ships the trace to the backend as a fire-and-forget
 * POST. Errors are swallowed — telemetry must never break the chat flow.
 *
 * Usage:
 *   const tracer = startChatTurn();
 *   tracer.mark('embedding', embeddingMs);
 *   const stopSearch = tracer.markStart('ai_search');
 *   // ... do the search ...
 *   stopSearch();
 *   tracer.setLlmMeta({ model: 'gpt-4o', finishReason: 'stop' });
 *   tracer.end({ agentId, conversationId });
 */

import { getBackendUrl } from '../config/runtimeConfig';
import { getBackendHeaders } from './backendAuth';

export type TraceStage =
  | 'client_prep'
  | 'file_parse'
  | 'vision_encode'
  | 'context_build'
  | 'embedding'
  | 'ai_search'
  | 'web_search'
  | 'summarize'
  | 'llm_total'
  | 'client_render'
  | 'save_conversation';

export interface ChatTurnTracer {
  readonly traceId: string;
  /** Record a pre-measured duration for a stage (e.g. from an external timer). */
  mark(stage: TraceStage, durationMs: number): void;
  /** Start a wall-clock timer; call the returned function to stop and record it. */
  markStart(stage: TraceStage): () => void;
  /** Record time-to-first-token for the LLM call. */
  setLlmTtft(ms: number): void;
  /** Record model name and finish reason after the LLM call completes. */
  setLlmMeta(meta: { model?: string; finishReason?: string }): void;
  /** Mark that the turn ended with an error. */
  setError(): void;
  /** Finalise the trace and ship it to the backend. */
  end(meta: { agentId?: string; conversationId?: string }): Promise<void>;
}

interface Segments {
  [stage: string]: number | null;
}

/**
 * Begin tracing a new chat turn. Call `end()` when the turn is complete
 * (whether or not it succeeded).
 */
export function startChatTurn(): ChatTurnTracer {
  const traceId = crypto.randomUUID();
  const startedAt = new Date();
  const startedNow = performance.now();
  const segments: Segments = {};
  let llmTtft: number | null = null;
  let llmModel: string | undefined;
  let llmFinishReason: string | undefined;
  let hadError = false;

  function mark(stage: TraceStage, durationMs: number): void {
    segments[stage] = Math.round(durationMs);
  }

  function markStart(stage: TraceStage): () => void {
    const t0 = performance.now();
    return (): void => {
      segments[stage] = Math.round(performance.now() - t0);
    };
  }

  function setLlmTtft(ms: number): void {
    llmTtft = Math.round(ms);
  }

  function setLlmMeta(meta: { model?: string; finishReason?: string }): void {
    llmModel = meta.model;
    llmFinishReason = meta.finishReason;
  }

  function setError(): void {
    hadError = true;
  }

  async function end(meta: { agentId?: string; conversationId?: string }): Promise<void> {
    const endedAt = new Date();
    const totalMs = Math.round(performance.now() - startedNow);
    if (llmTtft !== null) {
      segments.llm_ttft = llmTtft;
    }

    const body = {
      traceId,
      startedAt: startedAt.toISOString(),
      endedAt: endedAt.toISOString(),
      totalMs,
      segments,
      agentId: meta.agentId,
      conversationId: meta.conversationId,
      llmModel,
      llmFinishReason,
      hadError,
    };

    const backendUrl = getBackendUrl();
    if (!backendUrl) return;

    const serialisedBody = JSON.stringify(body);

    try {
      const headers = await getBackendHeaders();
      await fetch(`${backendUrl}/chat-traces`, {
        method: 'POST',
        headers,
        body: serialisedBody,
        keepalive: true,
      });
    } catch (err: unknown) {
      // Swallow all errors — telemetry must never break the chat flow.
      const msg = err instanceof Error ? err.message : 'Unknown error';
      console.warn('[chatTracing] Failed to ship trace:', msg);
    }
  }

  return {
    traceId,
    mark,
    markStart,
    setLlmTtft,
    setLlmMeta,
    setError,
    end,
  };
}
